import { useState, useEffect, useContext } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { AuthContext } from '../../contexts/AuthContext';
import './WeeklyDeliveryReport.css';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

const WeeklyDeliveryReport = () => {
  const { user } = useContext(AuthContext);
  const [rows, setRows]       = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState(null);

  useEffect(() => {
    axios
      .get(`${API_URL}/reports/weekly-delivery-summary`, {
        headers: { Authorization: `Bearer ${user?.token}` },
      })
      .then((r) => setRows(r.data))
      .catch((e) => setError(e.response?.data?.error || 'Failed to load'))
      .finally(() => setLoading(false));
  }, [user]);

  const maxAvg = Math.max(...rows.map((r) => r.avg_customers), 1);
  const overallAvg = rows.length
    ? Math.round(rows.reduce((s, r) => s + r.avg_customers, 0) / rows.filter((r) => r.avg_customers > 0).length || 0)
    : 0;

  return (
    <div className="wdr-page">
      <div className="wdr-header">
        <Link to="/reports" className="wdr-back">← Reports</Link>
        <h2 className="wdr-title">Day-wise Delivery Load</h2>
        <p className="wdr-sub">
          Avg distinct customers per day of week — based on next 4 weeks of PUB / PMB / KIT deliveries.
          Average counts only days that have scheduled deliveries.
        </p>
      </div>

      {loading && <div className="wdr-state">Loading…</div>}
      {error   && <div className="wdr-state wdr-error">{error}</div>}

      {!loading && !error && (
        <div className="wdr-body">
          <table className="wdr-table">
            <thead>
              <tr>
                <th>Day</th>
                <th className="wdr-num">Avg Customers</th>
                <th>Load</th>
                <th className="wdr-num">Active Weeks</th>
                <th className="wdr-num">4-Wk Total</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => {
                const pct = maxAvg > 0 ? Math.round((row.avg_customers / maxAvg) * 100) : 0;
                const isHigh = pct >= 75;
                const isMid  = pct >= 40 && pct < 75;
                return (
                  <tr key={row.dow}>
                    <td className="wdr-day">{row.day_name.trim()}</td>
                    <td className={`wdr-num wdr-avg-cell ${isHigh ? 'wdr-high' : isMid ? 'wdr-mid' : 'wdr-low'}`}>
                      {row.avg_customers > 0 ? row.avg_customers : '—'}
                    </td>
                    <td className="wdr-bar-cell">
                      {pct > 0 && (
                        <div className="wdr-bar-track">
                          <div
                            className={`wdr-bar-fill ${isHigh ? 'wdr-bar-high' : isMid ? 'wdr-bar-mid' : 'wdr-bar-low'}`}
                            style={{ width: `${pct}%` }}
                          />
                        </div>
                      )}
                    </td>
                    <td className="wdr-num wdr-muted">{row.active_days > 0 ? `${row.active_days} / 4` : '—'}</td>
                    <td className="wdr-num wdr-muted">{row.total_customers > 0 ? row.total_customers : '—'}</td>
                  </tr>
                );
              })}
            </tbody>
            <tfoot>
              <tr className="wdr-avg-row">
                <td>Weekly Avg</td>
                <td className="wdr-num">{overallAvg || '—'}</td>
                <td colSpan={3} />
              </tr>
            </tfoot>
          </table>

          <p className="wdr-hint">
            Days with low avg customers are good candidates for scheduling new PUB / PMB orders.
          </p>
        </div>
      )}
    </div>
  );
};

export default WeeklyDeliveryReport;
